import React, { useState, useRef } from 'react'
import styled from 'styled-components'
import { Popover, PopoverBody } from 'reactstrap'
import moment from 'moment'
import { FaChevronDown } from 'react-icons/fa'
import AlertTriangleIcon from '../../../assets/icons/icon-alert-triangle.svg'
import CloseIcon from '../../../assets/icons/icon-popover-close.svg'
import { LEVEL } from './utils/constants/levels'
import { ARTICLE_PATHS } from './utils/constants/articleUrls'
import { resolveUrlForArticles } from './utils/urlUtils'
import { formatRemainingTime } from './utils/displayUtils'
import { getSuspensionFromProgress, formatTimeRemaining, getSuspensionMessage, getBannerMessage } from './utils/suspensionUtils'
import { useMembershipJourneyContext } from './MembershipJourneyContext'
import {
  PopoverContainer,
  PopoverHeader,
  PopoverTitle,
  PopoverCloseButton,
  PopoverLevelSection,
  PopoverLevelTitle,
  PopoverLevelContent,
  PopoverDateRow,
  PopoverDateLabel,
  PopoverDateValue,
  PopoverNotStarted,
  PopoverDivider,
  PopoverButton,
  PopoverButtonText,
  PopoverGlobalStyles,
  StatusBadgeCheckmark
} from './Styles/ProgressTracker.styles'

const LEVEL_NAMES = {
  [LEVEL.QUALIFYING]: 'Qualifying',
  [LEVEL.LEVEL_1]: 'Level 1',
  [LEVEL.LEVEL_2]: 'Level 2',
  [LEVEL.LEVEL_3]: 'Level 3'
}

const SuspensionBanner = styled.div`
  width: 100%;
  padding: 6px 16px;
  background: #FFF3F2;
  border-radius: 8px;
  outline: 1px #D3180C solid;
  outline-offset: -1px;
  display: flex;
  align-items: center;
  gap: 8px;
`

const AlertIconWrapper = styled.div`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

const AlertIcon = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`

const BannerContent = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
`

const BannerTextSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const BannerTitle = styled.div`
  color: #00234B;
  font-size: 10px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
  word-wrap: break-word;
  line-height: 1.4;
  
  a {
    color: #D3180C;
    text-decoration: underline;
    font-weight: 600;
    
    &:hover {
      opacity: 0.8;
    }
  }
  
  strong {
    font-weight: 700;
    
    &.highlight {
      color: #D3180C;
    }
  }
`

const BannerDeadline = styled.div`
  color: #00234B;
  font-size: 10px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
  word-wrap: break-word;
`

const BannerDeadlineDate = styled.span`
  color: #D3180C;
  font-weight: 700;
`

const TimeRemainingValue = styled(PopoverDateValue)`
  color: ${props => props.isOverdue ? '#D32F2F' : '#00234B'};
  font-weight: ${props => props.isOverdue ? 600 : 500};
`

const BannerLink = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #D3180C;
  font-size: 11px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  background: none;
  border: none;
  padding: 0;
  
  &:hover {
    opacity: 0.8;
  }
  
  &:focus {
    outline: none;
  }
`

const ChevronIcon = styled(FaChevronDown)`
  width: 10px;
  height: 10px;
  color: #D3180C;
`

const PopoverWarningBanner = styled.div`
  width: 100%;
  padding: 6px 16px;
  background: #FFF3F2;
  border-radius: 8px;
  outline: 1px #D3180C solid;
  outline-offset: -1px;
  display: flex;
  align-items: center;
  gap: 8px;
`

const PopoverWarningContent = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`

const PopoverWarningInner = styled.div`
  flex: 1;
  padding: 4px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const PopoverWarningTextWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
`

const PopoverWarningText = styled.div`
  align-self: stretch;
  color: #00234B;
  font-size: 12px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
  word-wrap: break-word;
  line-height: 1.4;
  
  a {
    color: #D3180C;
    text-decoration: underline;
    font-weight: 600;
    
    &:hover {
      opacity: 0.8;
    }
  }
  
  strong {
    font-weight: 700;
    
    &.highlight {
      color: #D3180C;
    }
  }
`

const WarningIcon = styled.img`
  width: 12px;
  height: 12px;
  flex-shrink: 0;
`

const formatPopoverDisplayDate = (dateString) => {
  if (!dateString) return 'Not Started'
  return moment(dateString).format('MM/DD/YYYY')
}

const isDeadlineOverdue = (expirationDate) => {
  return expirationDate && moment(expirationDate).isBefore(moment())
}

const SuspensionWarningBar = () => {
  const [popoverOpen, setPopoverOpen] = useState(false)
  const targetRef = useRef(null)
  const { pgmProgress } = useMembershipJourneyContext()

  const suspensionData = getSuspensionFromProgress(pgmProgress)
  
  if (!suspensionData) {
    return null
  }

  const currentLevelKey = pgmProgress?.currentLevelKey
  const currentLevelName = LEVEL_NAMES[currentLevelKey] || 'Current Level'
  
  const bannerSuspensionMessage = getBannerMessage(suspensionData, currentLevelName)
  const popoverSuspensionMessage = getSuspensionMessage(suspensionData, currentLevelName)
  
  const toggle = () => setPopoverOpen(!popoverOpen)

  // Build all three levels from pgmProgress
  const levels = pgmProgress?.levels || []
  const levelKeys = [LEVEL.LEVEL_1, LEVEL.LEVEL_2, LEVEL.LEVEL_3]
  const levelsData = levelKeys.map(levelKey => {
    const levelData = levels.find(l => l.key === levelKey)
    
    if (levelData) {
      const isCompleted = levelData.status === 'complete'
      const isStarted = levelData.status !== 'locked'
      
      return {
        levelKey,
        levelName: LEVEL_NAMES[levelKey],
        isCompleted,
        isStarted,
        startDate: levelData.startDate,
        completionDate: levelData.completionDate,
        expirationDate: levelData.expirationDate
      }
    } else {
      return {
        levelKey,
        levelName: LEVEL_NAMES[levelKey],
        isCompleted: false,
        isStarted: false
      }
    }
  })

  const level1Data = levelsData.find(p => p.levelKey === LEVEL.LEVEL_1)
  const level3Data = levelsData.find(p => p.levelKey === LEVEL.LEVEL_3)
  const allLevelsComplete = levelsData.every(level => level.isCompleted)
  
  const associateProgram = {
    isCompleted: allLevelsComplete,
    startDate: level1Data?.startDate,
    completedDate: allLevelsComplete && level3Data?.completionDate ? level3Data.completionDate : null,
    expirationDate: pgmProgress?.programExpirationDate
  }

  return (
    <>
      <PopoverGlobalStyles />
      <SuspensionBanner>
        <AlertIconWrapper>
          <AlertIcon src={AlertTriangleIcon} alt="Alert" />
        </AlertIconWrapper>
        <BannerContent>
          <BannerTextSection>
            <BannerTitle>{bannerSuspensionMessage.title}</BannerTitle>
            {bannerSuspensionMessage.deadline && (
              <BannerDeadline>
                {bannerSuspensionMessage.deadlineLabel}:{' '}
                <BannerDeadlineDate>{bannerSuspensionMessage.deadline}</BannerDeadlineDate>
              </BannerDeadline>
            )}
          </BannerTextSection>
          <BannerLink
            ref={targetRef}
            onClick={toggle}
            id="suspensionWarningPopover"
          >
            Learn More
            <ChevronIcon />
          </BannerLink>
        </BannerContent>
      </SuspensionBanner>

      <Popover
        placement="bottom-end"
        isOpen={popoverOpen}
        target={targetRef}
        toggle={toggle}
        trigger="legacy"
        modifiers={{
          preventOverflow: { enabled: false },
          flip: { enabled: false }
        }}
      >
        <PopoverBody>
          <PopoverContainer>
            <PopoverHeader>
              <PopoverTitle>Associate Program Dates</PopoverTitle>
              <PopoverCloseButton onClick={toggle} aria-label="Close">
                <img src={CloseIcon} alt="" />
              </PopoverCloseButton>
            </PopoverHeader>

            {levelsData.map((level, index) => {
              const isLastLevel = index === levelsData.length - 1
              const timeRemaining = formatRemainingTime(level.expirationDate)
              const isCurrentLevel = level.levelKey === currentLevelKey
              const isOverdue = isDeadlineOverdue(level.expirationDate)
              
              return (
                <React.Fragment key={level.levelKey}>
                  <PopoverLevelSection>
                    <PopoverLevelTitle>
                      {level.isCompleted && <StatusBadgeCheckmark color="#2CA24B" />}
                      {isCurrentLevel && !level.isCompleted && <WarningIcon src={AlertTriangleIcon} alt="Warning" />}
                      {level.levelName}
                    </PopoverLevelTitle>
                    {isCurrentLevel && !level.isCompleted && (
                      <PopoverWarningBanner>
                        <PopoverWarningContent>
                          <PopoverWarningInner>
                            <PopoverWarningTextWrapper>
                              <PopoverWarningText>
                                {popoverSuspensionMessage}
                              </PopoverWarningText>
                            </PopoverWarningTextWrapper>
                          </PopoverWarningInner>
                        </PopoverWarningContent>
                      </PopoverWarningBanner>
                    )}
                    <PopoverLevelContent>
                      {level.isStarted ? (
                        <>
                          <PopoverDateRow>
                            <PopoverDateLabel>Started:</PopoverDateLabel>
                            <PopoverDateValue>
                              {formatPopoverDisplayDate(level.startDate)}
                            </PopoverDateValue>
                          </PopoverDateRow>
                          
                          {level.isCompleted ? (
                            <PopoverDateRow>
                              <PopoverDateLabel isCompleted>Completed:</PopoverDateLabel>
                              <PopoverDateValue isCompleted>
                                {formatPopoverDisplayDate(level.completionDate)}
                              </PopoverDateValue>
                            </PopoverDateRow>
                          ) : (
                            <>
                              <PopoverDateRow>
                                <PopoverDateLabel>Deadline:</PopoverDateLabel>
                                <PopoverDateValue>
                                  {formatPopoverDisplayDate(level.expirationDate)}
                                </PopoverDateValue>
                              </PopoverDateRow>
                              {isCurrentLevel && suspensionData?.hasTerminationWindow && suspensionData?.daysUntilTermination ? (
                                <PopoverDateRow>
                                  <PopoverDateLabel>Time Remaining:</PopoverDateLabel>
                                  <TimeRemainingValue isOverdue={isOverdue}>
                                    {isOverdue ? 'Overdue' : formatTimeRemaining(suspensionData)}
                                  </TimeRemainingValue>
                                </PopoverDateRow>
                              ) : (timeRemaining || isOverdue) && (
                                <PopoverDateRow>
                                  <PopoverDateLabel>Time Remaining:</PopoverDateLabel>
                                  <TimeRemainingValue isOverdue={isOverdue}>
                                    {isOverdue ? 'Overdue' : timeRemaining}
                                  </TimeRemainingValue>
                                </PopoverDateRow>
                              )}
                            </>
                          )}
                        </>
                      ) : (
                        <PopoverNotStarted>Not Started</PopoverNotStarted>
                      )}
                    </PopoverLevelContent>
                  </PopoverLevelSection>
                  {!isLastLevel && <PopoverDivider />}
                </React.Fragment>
              )
            })}
            <PopoverDivider />

            <PopoverLevelSection isLastSection>
              <PopoverLevelTitle>
                {associateProgram.isCompleted && <StatusBadgeCheckmark color="#2CA24B" />}
                Associate Program
              </PopoverLevelTitle>
              <PopoverLevelContent>
                {associateProgram.startDate && (
                  <PopoverDateRow>
                    <PopoverDateLabel>Started:</PopoverDateLabel>
                    <PopoverDateValue>
                      {formatPopoverDisplayDate(associateProgram.startDate)}
                    </PopoverDateValue>
                  </PopoverDateRow>
                )}
                {associateProgram.isCompleted ? (
                  <PopoverDateRow>
                    <PopoverDateLabel isCompleted>Completed:</PopoverDateLabel>
                    <PopoverDateValue isCompleted>
                      {formatPopoverDisplayDate(associateProgram.completedDate)}
                    </PopoverDateValue>
                  </PopoverDateRow>
                ) : (
                  <>
                    {associateProgram.expirationDate && (
                      <PopoverDateRow>
                        <PopoverDateLabel>Deadline:</PopoverDateLabel>
                        <PopoverDateValue>
                          {formatPopoverDisplayDate(associateProgram.expirationDate)}
                        </PopoverDateValue>
                      </PopoverDateRow>
                    )}
                  </>
                )}
              </PopoverLevelContent>
            </PopoverLevelSection>

            <PopoverButton
              href={resolveUrlForArticles(ARTICLE_PATHS.TIMEFRAME_DEADLINES)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <PopoverButtonText>Acceptable Progress Guide</PopoverButtonText>
            </PopoverButton>
          </PopoverContainer>
        </PopoverBody>
      </Popover>
    </>
  )
}

export default SuspensionWarningBar
